import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../contexts/ThemeContext";
import { downloadService } from "../../services/downloadService";

const UserDownloadLogs = ({ open, onClose, userId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { themeMode } = useContext(ThemeContext);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    status: "all",
    productVersion: "all",
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await downloadService.getUserDownloads(userId, filters);
      setLogs(response);
      setError(null);
    } catch (err) {
      setError("Failed to fetch download logs");
      console.error("Error fetching download logs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && userId) {
      fetchLogs();
    }
  }, [open, userId, filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      default:
        return "warning";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Download Logs</Typography>
          <IconButton onClick={fetchLogs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                slotProps={{ textField: { size: "small" } }}
                onChange={(date) => handleFilterChange("startDate", date)}
                renderInput={(params) => <TextField {...params} />}
              />
              <DatePicker
                label="End Date"
                value={filters.endDate}
                slotProps={{ textField: { size: "small" } }}
                onChange={(date) => handleFilterChange("endDate", date)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="started">Started</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Product Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>User Agent</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, index) => (
                <TableRow
                  key={log.id}
                  hover
                  sx={{
                    "&:hover": {
                      borderBottomColor: "primary.main",
                    },
                    "& td": {
                      py: 2,
                    },
                  }}
                >
                  <TableCell>
                    {new Date(log.started_at).toLocaleString()}
                  </TableCell>
                  <TableCell>{log.ProductVersion.version || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.status}
                      color={getStatusColor(log.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{log.ip_address || "N/A"}</TableCell>
                  <TableCell>
                    <Tooltip title={log.user_agent || "N/A"}>
                      <Typography noWrap sx={{ maxWidth: 200 }}>
                        {log.user_agent || "N/A"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {log.completed_at
                      ? `${Math.round(
                          (new Date(log.completed_at) -
                            new Date(log.started_at)) /
                            1000,
                        )}s`
                      : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ bgColor: "error" }}
                  >
                    No download logs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDownloadLogs;
